import { useCallback, useEffect, useState } from "react";

export type ThemeName = "dark" | "light";
const STORAGE_KEY = "magi.theme";

/**
 * Resolve the theme for first render.
 *
 * Priority:
 *   1. Stored user choice (sticky override).
 *   2. OS `prefers-color-scheme` if it expresses one.
 *   3. `initial` — the config default passed in.
 */
function resolveInitialTheme(initial: ThemeName): ThemeName {
  if (typeof window === "undefined") return initial;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "dark" || stored === "light") return stored;
  if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) return "dark";
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  return initial;
}

export function useTheme(initial: ThemeName = "dark") {
  const [theme, setThemeState] = useState<ThemeName>(() =>
    resolveInitialTheme(initial),
  );

  // Apply the theme attribute to <html>. Persistence is NOT done here —
  // writing on every render would mark an OS-derived theme as an explicit
  // user choice, breaking the follow-OS behaviour below.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Explicit user choice — persist so the toggle is sticky across reloads
  // and so the OS-change listener below knows to stop following the OS.
  const setTheme = useCallback((next: ThemeName) => {
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage blocked */
    }
  }, []);

  const toggle = useCallback(() => {
    setThemeState((t) => {
      const next: ThemeName = t === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage blocked */
      }
      return next;
    });
  }, []);

  // Follow live OS theme flips while the user has no stored choice. Once
  // they explicitly toggle, localStorage becomes non-empty and this listener
  // leaves the theme alone.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const onChange = (e: MediaQueryListEvent) => {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
      setThemeState(e.matches ? "dark" : "light");
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return { theme, setTheme, toggle };
}
